/**
 * Tema chiaro/scuro.
 *
 * Tre valori memorizzati, due resi:
 *   'auto'  segue il sistema (`prefers-color-scheme`) — e continua a seguirlo
 *           anche se l'utente cambia impostazione a pagina aperta
 *   'light' | 'dark'  scelta esplicita, vince sul sistema
 *
 * Sull'elemento radice finisce sempre e solo il tema *risolto*, come
 * `data-theme='light' | 'dark'`: il CSS non deve sapere che 'auto' esiste.
 * I token stanno in styles/tokens.css (ruoli globali) e styles/stitch.css
 * (token --st-*). Vedi anche lo script inline in index.html, che scrive lo
 * stesso attributo prima del mount per non far lampeggiare la pagina.
 *
 * La preferenza sta in `localStorage`, non in DB: e' del browser, non
 * dell'utente del portale.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

export type ThemeChoice = 'auto' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'mm_theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

function isChoice(v: unknown): v is ThemeChoice {
  return v === 'auto' || v === 'light' || v === 'dark';
}

/** `localStorage` lancia in navigazione privata: il tema non vale un crash. */
function readStored(): ThemeChoice {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isChoice(stored) ? stored : 'auto';
  } catch {
    return 'auto';
  }
}

function systemTheme(): ResolvedTheme {
  return window.matchMedia?.(DARK_QUERY).matches ? 'dark' : 'light';
}

type Ctx = {
  /** Cosa ha scelto l'utente, 'auto' incluso. */
  choice: ThemeChoice;
  /** Cosa si vede davvero adesso. */
  theme: ResolvedTheme;
  setChoice: (c: ThemeChoice) => void;
  /** Passa all'opposto di quello che si vede, fissandolo. */
  toggle: () => void;
};

const ThemeContext = createContext<Ctx | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [choice, setChoiceState] = useState<ThemeChoice>(readStored);
  const [system, setSystem] = useState<ResolvedTheme>(systemTheme);

  // Il sistema puo' cambiare a pagina aperta (tramonto su macOS, tema di
  // Windows): con 'auto' la pagina deve seguirlo senza ricaricare.
  useEffect(() => {
    const mq = window.matchMedia?.(DARK_QUERY);
    if (!mq) return;
    const onChange = (e: MediaQueryListEvent) => setSystem(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const theme: ResolvedTheme = choice === 'auto' ? system : choice;

  /*
   * Il cambio tema e' animato, ma solo mentre avviene: `data-theme-switching`
   * accende per 300ms la transizione globale in styles/global.css e poi si
   * spegne. Una transizione lasciata sempre accesa su tutti gli elementi
   * rallenterebbe hover e cambi di stato del resto dell'interfaccia.
   *
   * Il primo giro non anima: al caricamento il tema c'e' gia' (lo scrive lo
   * script in index.html) e non e' un cambio, e' lo stato di partenza.
   */
  const primoGiro = useRef(true);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset['theme'] = theme;

    if (primoGiro.current) {
      primoGiro.current = false;
      return;
    }

    root.dataset['themeSwitching'] = '';
    const t = window.setTimeout(() => {
      delete root.dataset['themeSwitching'];
    }, 300);
    return () => window.clearTimeout(t);
  }, [theme]);

  const setChoice = useCallback((next: ThemeChoice) => {
    setChoiceState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Preferenza non persistita: la sessione corrente funziona comunque.
    }
  }, []);

  const value = useMemo<Ctx>(
    () => ({
      choice,
      theme,
      setChoice,
      toggle: () => setChoice(theme === 'dark' ? 'light' : 'dark'),
    }),
    [choice, theme, setChoice],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): Ctx {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme fuori da ThemeProvider');
  return ctx;
}
